import { QueryClientProvider } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { createRoot } from 'react-dom/client'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { useMediaQuery } from 'usehooks-ts'
import BackgroundSvg from '~/assets/usecase-fork/Background.svg?react'
import RocketSvg from '~/assets/usecase-fork/rocket.svg?react'
import { useSetAccountPreferences } from '~/hooks/users/useSetAccountPreferences'
import { Providers, trackEvent } from '~/providers'
import { queryClient } from '~/providers/ReactQuery'
import { FormContainer } from './components/FormContainer'
import { CalendarBooking } from './components/CalendarBooking'
import { WelcomeScreen } from './components/WelcomeScreen'
import { FormData } from './types'

/**
 * USER SEGMENTATION SURVEY FLOW
 *
 * ┌─────────────────────────────────────────────────────────┐
 * │           User sees User Segmentation Screen            │
 * └─────────────────┬───────────────────────────────────────┘
 *                   │
 *        ┌──────────┴──────────┐
 *        │                     │
 *   "I want to build"    "Build my agent"
 *        │                     │
 *        v                     v
 *  Welcome Screen      Onboarding Form
 *        │              (role, useCase, etc.)
 *        │                     │
 *        │                     v
 *        │            Check eligibility
 *        │                     │
 *        │         ┌───────────┴────────────────┐
 *        │         │                            │
 *        │   Student/Learner OR            All others
 *        │   Just me OR                         │
 *        │   IN/PK/BD timezone                  v
 *        │         │                    Calendar Booking
 *        │         v                            │
 *        │   Welcome Screen    ┌────────────────┴─────┐
 *        │         │           │                      │
 *        │         │     Book meeting               Skip
 *        │         │   (auto-detected)                │
 *        │         │           │                      │
 *        │         │           v                      │
 *        │         │     Welcome Screen               │
 *        │         │           │                      │
 *        └─────────┴───────────┴──────────────────────┘
 *                              │
 *                              v
 *                        Dashboard
 *
 */
const POST_SIGNUP_FORK_ID = 'post-signup-fork'

export function UserSegmentationSurvey({ onDone, workspaceId }: { onDone: () => void; workspaceId: string }) {
  const { mutate: setPreferences } = useSetAccountPreferences()
  const [showForm, setShowForm] = useState(false)
  const [showCalendar, setShowCalendar] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const handleIWantToBuild = () => {
    setPreferences({ path: 'requireOnboarding', value: false })
    trackEvent({ type: 'post_signup_fork_selected', choice: 'I want to build' })
    setShowWelcome(true)
  }

  const handleBuildMyAgent = () => {
    setPreferences({ path: 'requireOnboarding', value: false })
    trackEvent({ type: 'post_signup_fork_selected', choice: 'build for me' })
    setShowForm(true)
  }

  const handleFormComplete = (completedFormData: Partial<FormData>) => {
    // Get user's timezone to determine if they're from excluded regions
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const excludedTimezones = [
      // India
      'Asia/Kolkata',
      'Asia/Calcutta',
      // Pakistan
      'Asia/Karachi',
      // Bangladesh
      'Asia/Dhaka',
    ]
    const isFromExcludedRegion = excludedTimezones.includes(userTimezone)

    // Check if user should see calendar (NOT student/learner AND NOT just-me AND NOT from excluded regions)
    const shouldShowCalendar =
      completedFormData.role !== 'Student / Learner' &&
      completedFormData.companySize !== 'Just me' &&
      !isFromExcludedRegion

    if (shouldShowCalendar) {
      setShowForm(false)
      setShowCalendar(true)
    } else {
      setShowForm(false)
      setShowWelcome(true)
    }
  }

  const handleCalendarComplete = () => {
    setShowCalendar(false)
    setShowWelcome(true)
  }

  const handleWelcomeComplete = () => {
    setShowWelcome(false)
    onDone()
  }

  return (
    <div className="fixed inset-0 z-[90] flex flex-col bg-black p-4 md:flex-row md:pb-6 md:pl-0 md:pr-6 md:pt-6">
      {/* Left Side - For Developers - Hidden on mobile when form is shown */}
      {(!showForm || isDesktop) && (
        <div className="relative flex h-1/2 w-full flex-col items-start justify-center bg-black px-6 py-8 md:h-full md:w-1/2 md:px-[72px] md:py-0">
          {/* Background SVG */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <BackgroundSvg className="absolute inset-0 h-full w-full" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex w-full max-w-[648px] flex-col gap-6 md:gap-12">
            <div className="flex flex-col gap-4 md:gap-10">
              <p className="text-xs font-light leading-tight text-gray-100 md:text-base md:leading-snug">
                FOR DEVELOPERS
              </p>
              <h1 className="max-w-[70%] text-2xl font-normal leading-[1.1] text-white md:text-[72px] md:leading-[72px]">
                I'm here to build with AI.
              </h1>
              <p className="max-w-[70%] text-xs text-gray-100 md:text-base">
                Get everything you need to build and scale. Botpress Studio, APIs & SDKs, hosting and deployment tools,
                and a full monitoring dashboard.
              </p>
            </div>

            <button
              onClick={handleIWantToBuild}
              className="flex h-12 w-fit items-center gap-2 bg-[#dcc663] px-6 py-2 text-xs text-black shadow-sm transition-opacity hover:opacity-90 md:h-[88px] md:px-8 md:text-base"
            >
              Start building
              <ArrowRight className="size-4 md:size-8" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}

      {/* Center Divider - Desktop Only */}
      {!showForm && (
        <div className="absolute left-[calc(50%-12px)] top-1/2 z-20 hidden size-[80px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gray-300 md:flex">
          <span className="text-lg font-normal tracking-tight text-black">OR</span>
        </div>
      )}

      {/* FormContainer - Behind right card on desktop, replaces everything on mobile */}
      {showForm && (
        <div
          className={`h-full w-full overflow-auto bg-black ${isDesktop ? 'absolute right-0 top-0 z-0 rounded-[10px] md:w-1/2' : 'fixed inset-0 z-[100]'}`}
        >
          <FormContainer onComplete={handleFormComplete} workspaceId={workspaceId} />
        </div>
      )}

      {/* Calendar Booking - Full screen overlay */}
      {showCalendar && <CalendarBooking onComplete={handleCalendarComplete} />}

      {/* Welcome Screen - Full screen overlay */}
      {showWelcome && <WelcomeScreen onComplete={handleWelcomeComplete} />}

      {/* Right Side - For Others (Card Style) - Hidden on mobile when form is shown */}
      {(!showForm || isDesktop) && (
        <motion.div
          className="relative z-10 mt-4 flex h-1/2 w-full flex-col items-start justify-center rounded-[20px] bg-[#e6e1d8] px-6 py-8 md:mt-0 md:h-full md:w-1/2 md:rounded-[10px] md:px-[72px] md:py-0"
          animate={
            showForm && isDesktop
              ? {
                  x: '-97%',
                  transition: { duration: 0.7, ease: 'easeInOut' },
                }
              : { x: 0 }
          }
        >
          {/* Rocket Illustration */}
          <div className="pointer-events-none absolute right-4 top-8 md:right-[16px] md:top-[43px]">
            <RocketSvg className="h-[200px] w-[175px] opacity-30 md:h-[406px] md:w-[351px] md:opacity-100" />
          </div>

          {/* Content */}
          <div className="relative z-10 flex w-full max-w-[593px] flex-col gap-6 md:gap-12">
            <div className="flex flex-col gap-4 md:gap-10">
              <p className="text-xs font-light text-black md:text-base">FOR OTHERS</p>
              <h1
                className="max-w-[70%] text-2xl font-normal leading-[1.1] text-black md:text-[72px] md:leading-[72px]"
                style={{ fontFamily: 'Times New Roman, serif' }}
              >
                I want someone to build for me.
              </h1>
              <p className="max-w-[70%] text-xs text-black md:text-base">
                Botpress will work with you to get your AI projects to production.
              </p>
            </div>

            <button
              onClick={handleBuildMyAgent}
              disabled={showForm}
              className="flex h-12 w-fit items-center gap-2 rounded-full bg-[#394c8f] px-6 py-2 text-xs text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40 disabled:grayscale md:h-[88px] md:px-8 md:text-base"
            >
              Build my agent
              <ArrowRight className="size-4 md:size-8" strokeWidth={1.5} />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export async function launchUserSegmentationSurvey(workspaceId: string): Promise<void> {
  if (document.getElementById(POST_SIGNUP_FORK_ID)) {
    // Fork already launched
    return
  }

  const container = document.createElement('div')
  container.id = POST_SIGNUP_FORK_ID

  document.getElementById('root')!.appendChild(container)
  const removeContainer = () => {
    const element = document.getElementById('root')
    if (element && container.parentNode === element) {
      element.removeChild(container)
    }
  }

  const root = createRoot(container)

  return new Promise<void>((resolve) => {
    root.render(
      <Providers>
        <QueryClientProvider client={queryClient}>
          <UserSegmentationSurvey
            workspaceId={workspaceId}
            onDone={() => {
              try {
                removeContainer()
                root.unmount()
              } finally {
                resolve()
              }
            }}
          />
        </QueryClientProvider>
      </Providers>
    )
  })
}
